import useModal from 'core/hooks/ui/useModal'
import Button, { ButtonProps } from 'core/components/buttons/Button'
import { useMemo } from 'react'
import Modal from 'core/components/dialogs/Modal'
import { MemberRow, OnboardingInvitePayload } from 'types/entity'
import useBatchInvite from 'core/hooks/members/useBatchInvite'
import { useQueryClient } from '@tanstack/react-query'
import { pluralizeWord } from 'utils/Functions'

interface SendInviteActionProps {
  selectedMembers?: string[] | MemberRow['token_id'][]
  needsAcceptInvite?: boolean
  needsInvite?: boolean
  onlyMembers?: boolean
  variant?: ButtonProps['variant']
  className?: string
  buttonTitle: string
  onSuccess?: VoidFunction
  forceInvite?: boolean
  totalCount?: number
}

const SendInviteAction = ({
  selectedMembers,
  className,
  variant,
  buttonTitle,
  onSuccess,
  needsAcceptInvite,
  needsInvite,
  onlyMembers,
  forceInvite,
  totalCount
}: SendInviteActionProps) => {
  const { open, openModal, closeModal } = useModal()
  const queryClient = useQueryClient()
  const { sendInviteQuery } = useBatchInvite({
    onSuccess: () => {
      onSuccess?.()
      closeModal()
      queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    }
  })

  const { mutate: upload, isPending: isLoading } = sendInviteQuery

  const invitesPayload = useMemo((): OnboardingInvitePayload => {
    const _payload: OnboardingInvitePayload = {
      filter_params: {}
    }

    if (selectedMembers?.length) {
      _payload.filter_params.member_token_ids = selectedMembers
    }

    if (needsAcceptInvite || needsAcceptInvite === false) {
      _payload.filter_params.needs_accept_invite = needsAcceptInvite
    }

    if (needsInvite || needsInvite === false) {
      _payload.filter_params.needs_invite = needsInvite
    }

    if (onlyMembers || onlyMembers === false) {
      _payload.only_members = onlyMembers
    }

    if (forceInvite || forceInvite === false) {
      _payload.force_invite = forceInvite
    }

    return _payload
  }, [
    forceInvite,
    needsAcceptInvite,
    needsInvite,
    onlyMembers,
    selectedMembers
  ])

  const count = totalCount || selectedMembers?.length || 0
  return (
    <>
      <Modal
        show={open}
        onClose={closeModal}
        title={`${buttonTitle.toLocaleLowerCase().includes('re-send') ? 'Re-send' : 'Send'} Everlance invite to ${count} ${pluralizeWord(
          {
            count,
            singular: 'member',
            plural: 'members'
          }
        )}?`}
        footer={
          <Button
            variant="primary"
            status={isLoading ? 'loading' : 'idle'}
            onClick={() => {
              upload(invitesPayload)
            }}
          >
            {buttonTitle} now
          </Button>
        }
      >
        <p>
          Members will receive a welcome email with instructions on how to
          download the mobile app and get set up with Everlance.
        </p>
      </Modal>

      {totalCount || selectedMembers?.length ? (
        <Button variant={variant} onClick={openModal} className={className}>
          {buttonTitle}
          {variant === 'primary' && totalCount ? ` (${totalCount})` : ''}
        </Button>
      ) : (
        <span className="h-10 block">&nbsp;</span>
      )}
    </>
  )
}

export default SendInviteAction
